import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { authApi, familyApi } from "../services/api";
import GoogleAuthButton from "../components/shared/GoogleAuthButton";

export default function LoginPage({ initialMode = "signin" }) {
  const { login, register, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const inviteToken = searchParams.get("invite") || "";
  const initialEmail = searchParams.get("email") || "";
  const redirectParam = searchParams.get("redirect") || "";
  const safeRedirect = redirectParam.startsWith("/") && !redirectParam.startsWith("//") ? redirectParam : "";
  const requestedMode = searchParams.get("mode");
  const [mode, setModeState] = useState(requestedMode === "create" ? "create" : initialMode);
  const [email, setEmail] = useState(initialEmail);
  const [fullName, setFullName] = useState("");
  const [password, setPassword] = useState("");
  const [ageConfirmed, setAgeConfirmed] = useState(false);
  const [error, setError] = useState("");
  const [resetNotice, setResetNotice] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sendingReset, setSendingReset] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const isCreateMode = mode === "create";

  function setMode(nextMode) {
    setModeState(nextMode);
    setError("");
    setResetNotice(null);
    const nextParams = new URLSearchParams(searchParams);
    if (nextMode === "create") {
      nextParams.set("mode", "create");
    } else {
      nextParams.delete("mode");
    }
    if (email) {
      nextParams.set("email", email);
    } else {
      nextParams.delete("email");
    }
    setSearchParams(nextParams, { replace: true });
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");

    if (isCreateMode && password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }

    if (isCreateMode && !ageConfirmed) {
      setError("Please confirm you are 13 or older to create an account.");
      return;
    }

    setLoading(true);
    try {
      if (isCreateMode) {
        await register({
          full_name: fullName,
          email,
          password,
          age_confirmed: ageConfirmed,
        });
      } else {
        await login(email, password);
      }
      if (inviteToken) {
        await familyApi.acceptInvite(inviteToken);
        await refreshUser?.();
        navigate("/app/family");
      } else if (isCreateMode) {
        navigate("/welcome");
      } else {
        navigate(safeRedirect || "/app");
      }
    } catch (err) {
      setError(err.message || (isCreateMode ? "Registration failed" : "Login failed"));
    } finally {
      setLoading(false);
    }
  }

  async function handleForgotPassword() {
    if (!email.trim()) {
      setError("Enter your email first, then choose forgot password.");
      return;
    }
    setError("");
    setResetNotice(null);
    setSendingReset(true);
    try {
      const res = await authApi.forgotPassword({ email });
      setResetNotice({
        message: res.message || "If that email has a HouseMind account, a reset link is on the way.",
        resetToken: res.reset_token,
      });
    } catch (err) {
      setError(err.message || "Unable to send a reset link right now.");
    } finally {
      setSendingReset(false);
    }
  }

  return (
    <div className="hm-auth-shell">
      <div className="hm-auth-card">
        <div className="hm-auth-brand">
          <strong>HouseMind</strong>
        </div>

        <div className="hm-auth-tabs" role="tablist" aria-label="Choose auth flow">
          <button
            type="button"
            className={mode === "signin" ? "is-active" : ""}
            onClick={() => setMode("signin")}
            role="tab"
            aria-selected={mode === "signin"}
          >
            Sign In
          </button>
          <button
            type="button"
            className={mode === "create" ? "is-active" : ""}
            onClick={() => setMode("create")}
            role="tab"
            aria-selected={mode === "create"}
          >
            Create Account
          </button>
        </div>

        <h1 className="hm-auth-title">{isCreateMode ? "Create account" : "Welcome back to HouseMind"}</h1>

        {error && <div className="hm-auth-error">{error}</div>}
        {resetNotice && (
          <div
            className="hm-auth-error"
            style={{
              background: "rgba(42,123,111,0.10)",
              borderColor: "rgba(42,123,111,0.28)",
              color: "var(--hm-cyan)",
            }}
          >
            <div>{resetNotice.message}</div>
            {resetNotice.resetToken && (
              <div style={{ marginTop: 8 }}>
                Dev reset link:{" "}
                <Link to={`/reset-password?token=${resetNotice.resetToken}`} className="hm-auth-link">
                  open reset page
                </Link>
              </div>
            )}
          </div>
        )}

        <form onSubmit={handleSubmit} className="hm-auth-form">
          <div className={`hm-auth-create-fields${isCreateMode ? " is-open" : ""}`} aria-hidden={!isCreateMode}>
            <label className="hm-auth-label">
              Full name
              <input
                type="text"
                className="hm-auth-input"
                value={fullName}
                onChange={e => setFullName(e.target.value)}
                required={isCreateMode}
                autoFocus={isCreateMode}
                autoComplete="name"
                placeholder="Enter your full name"
                tabIndex={isCreateMode ? 0 : -1}
              />
            </label>
          </div>

          <label className="hm-auth-label">
            Email
            <input
              type="email"
              className="hm-auth-input"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
              autoFocus={!isCreateMode}
              autoComplete="email"
              placeholder="Enter your email"
            />
          </label>

          <label className="hm-auth-label">
            Password
            <div className="password-field">
              <input
                type={showPassword ? "text" : "password"}
                className="hm-auth-input"
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
                autoComplete={isCreateMode ? "new-password" : "current-password"}
                minLength={isCreateMode ? 8 : undefined}
                placeholder="Enter your password"
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(v => !v)}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? "Hide" : "Show"}
              </button>
            </div>
          </label>

          {!isCreateMode && <div style={{ textAlign: "right", marginTop: -6 }}>
            <button
              type="button"
              className="hm-auth-link"
              style={{ background: "none", border: 0, padding: 0, cursor: "pointer", fontSize: 13 }}
              disabled={sendingReset}
              onClick={handleForgotPassword}
            >
              {sendingReset ? "Sending reset link..." : "Forgot password?"}
            </button>
          </div>}

          <div className={`hm-auth-create-fields${isCreateMode ? " is-open" : ""}`} aria-hidden={!isCreateMode}>
            <label className="hm-auth-check">
              <input
                type="checkbox"
                checked={ageConfirmed}
                onChange={e => setAgeConfirmed(e.target.checked)}
                required={isCreateMode}
                tabIndex={isCreateMode ? 0 : -1}
              />
              <span>By creating an account, I confirm I am 13 years of age or older.</span>
            </label>
          </div>

          <button type="submit" className="hm-auth-btn" disabled={loading}>
            {loading ? (isCreateMode ? "Creating account…" : "Signing in…") : (isCreateMode ? "Create account" : "Sign in")}
          </button>
        </form>

        <div className="hm-auth-divider"><span>or</span></div>

        <GoogleAuthButton
          redirectTo={inviteToken ? "/app/family" : (safeRedirect || "/app")}
          inviteToken={inviteToken}
          ageConfirmed={isCreateMode ? ageConfirmed : false}
          onError={setError}
        />
      </div>
    </div>
  );
}
